import { Box, Chip, Paper, Stack, Typography, useTheme, Button } from '@mui/material';
import { MdVerified } from 'react-icons/md';
import { getLanguageNameByCode, titleCase } from 'utils/helperFunctions';
import { useEffect, useState } from 'react';
import { IoMdPlay } from 'react-icons/io';
import { IoIosPause } from 'react-icons/io';
import { LibraryVoice } from './types';
import VoiceCardLoader from './VoiceCardLoader';

type Props = {
  voice: LibraryVoice;
  listingView?: boolean;
  isSelected?: boolean;
  onSelect?: any;
  isDefaultVoice?: boolean;
  isVoiceLoading?: boolean;
  setOpen?: any;
};

// shared audio & currently playing voiceId across all cards
let currentAudio: HTMLAudioElement | null = null;
let currentVoiceId: string | null = null;

const VoiceCard = ({
  voice,
  listingView = false,
  isSelected,
  onSelect,
  setOpen,
  isVoiceLoading,
  isDefaultVoice
}: Props) => {
  const theme = useTheme();
  const [playing, setPlaying] = useState(false);
  const language = isDefaultVoice ? voice.labels?.language : voice.language;
  const accent = isDefaultVoice ? voice.labels?.accent : voice.accent;
  const age = isDefaultVoice ? voice.labels?.age : voice.age;
  const gender = isDefaultVoice ? voice.labels?.gender : voice.gender;

  const handlePlay = async () => {
    const url = voice.preview_url;
    if (!url) return;

    // if this card's voice is already playing then stop it
    if (currentVoiceId === voice.voice_id && currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio = null;
      currentVoiceId = null;
      setPlaying(false);
      return;
    }

    // stop any other voice that's currently playing
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio = null;
      currentVoiceId = null;
    }

    // create and play new audio
    const newAudio = new Audio(url);
    currentAudio = newAudio;
    currentVoiceId = voice.voice_id;

    newAudio.onended = () => {
      if (currentVoiceId === voice.voice_id) {
        setPlaying(false);
        currentAudio = null;
        currentVoiceId = null;
      }
    };

    try {
      await newAudio.play();
      setPlaying(true);
    } catch (err) {
      console.error('Audio playback failed', err);
      setPlaying(false);
      currentAudio = null;
      currentVoiceId = null;
    }
  };

  // keep local state in sync with global currentVoiceId
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaying(currentVoiceId === voice.voice_id);
    }, 200);
    return () => clearInterval(interval);
  }, [voice.voice_id]);

  // cleanup when card unmounts
  useEffect(() => {
    return () => {
      if (currentVoiceId === voice.voice_id && currentAudio) {
        currentAudio.pause();
        currentAudio = null;
        currentVoiceId = null;
      }
    };
  }, [voice.voice_id]);

  if (isVoiceLoading) {
    return <VoiceCardLoader />;
  }
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${isSelected ? theme.palette.primary.main : '#ececec'}`,
        px: 2,
        py: 1,
        mt: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2
      }}
    >
      {/* Content */}
      <Box display='flex' flexDirection='row' justifyContent='space-between' width='100%' gap={2}>
        <Box width='100%'>
          <Typography variant='subtitle1' fontWeight={500} color={theme.palette.primary.main}>
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              gap={0.5}
              justifyContent='space-between'
              width='100%'
            >
              <Box display='flex' flexDirection='row' gap={1} alignItems='center'>
                <Typography fontWeight={500} fontSize='16px'>
                  {voice.name}
                </Typography>
                <MdVerified size={18} color={theme.palette.primary.main} />
              </Box>
              <Stack direction='row' spacing={1}>
                {/* Play Button (circular) */}
                {voice.preview_url && (
                  <Button
                    size='small'
                    variant={playing ? 'contained' : 'outlined'}
                    onClick={handlePlay}
                    sx={{
                      minWidth: 32,
                      width: 32,
                      height: 32,
                      minHeight: '5px',
                      maxHeight: '32px',
                      borderRadius: '50%',
                      p: 0,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&.MuiButton-contained': {
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff'
                      }
                    }}
                  >
                    {playing ? <IoIosPause size={16} /> : <IoMdPlay size={16} />}
                  </Button>
                )}

                {/* Select Button (pill-shaped) */}
                {listingView ? (
                  <Button
                    size='small'
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={() => onSelect?.(voice)}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      borderRadius: '999px',
                      fontSize: '0.75rem',
                      minHeight: '5px',
                      maxHeight: '32px',
                      textTransform: 'none',
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&.MuiButton-contained': {
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff'
                      }
                    }}
                  >
                    {isSelected ? 'Selected' : 'Select'}
                  </Button>
                ) : (
                  <Button
                    variant='outlined'
                    sx={{
                      minWidth: '60px',
                      minHeight: '5px',
                      maxHeight: '32px',
                      px: 0,
                      borderRadius: '999px',
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&.MuiButton-contained': {
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff'
                      }
                    }}
                    onClick={() => setOpen(true)}
                  >
                    Edit
                  </Button>
                )}
              </Stack>
            </Box>
          </Typography>
          <Typography variant='body2' mt={0.5} color='text.secondary'>
            {voice.description ?? 'No description available.'}
          </Typography>
          <Stack direction='row' spacing={1} mt={1} flexWrap='wrap'>
            {language && (
              <Chip size='small' sx={{ bgcolor: '#F8F9FA' }} label={`language: ${getLanguageNameByCode(language)}`} />
            )}
            {accent && <Chip size='small' sx={{ bgcolor: '#F8F9FA' }} label={`accent: ${accent}`} />}
            {age && <Chip size='small' sx={{ bgcolor: '#F8F9FA' }} label={`age: ${titleCase(age)}`} />}
            {gender && <Chip size='small' sx={{ bgcolor: '#F8F9FA' }} label={`gender: ${gender}`} />}
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};

export default VoiceCard;
